import { z } from 'zod';

/**
 * P01 iPhone-proof spike. Throwaway: removed in P16 when real capture replaces it.
 * These shapes are shared by the server and the test pages.
 */

export const spikeStatusSchema = z.object({ enabled: z.boolean() });
export type SpikeStatus = z.infer<typeof spikeStatusSchema>;

export const spikeConfigSchema = z.object({
  vapidPublicKey: z.string(),
  captureToken: z.string(),
});
export type SpikeConfig = z.infer<typeof spikeConfigSchema>;

export const SPIKE_KINDS = ['share', 'note', 'wallet', 'push_sent', 'push_error'] as const;
export type SpikeKind = (typeof SPIKE_KINDS)[number];

export const spikeReceivedItemSchema = z.object({
  id: z.string(),
  kind: z.enum(SPIKE_KINDS),
  payload: z.unknown(),
  receivedAt: z.iso.datetime(),
});
export type SpikeReceivedItem = z.infer<typeof spikeReceivedItemSchema>;

export const spikeReceivedListSchema = z.object({ items: z.array(spikeReceivedItemSchema) });

/** What the browser's pushManager.subscribe() gives us (PushSubscription.toJSON()). */
export const pushSubscriptionSchema = z.object({
  endpoint: z
    .string()
    .max(2048)
    .refine((v) => v.startsWith('https://'), 'endpoint must be https'),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1).max(256),
    auth: z.string().min(1).max(64),
  }),
});
export type PushSubscriptionData = z.infer<typeof pushSubscriptionSchema>;

export const pushTestRequestSchema = z.object({
  delaySeconds: z.union([z.literal(0), z.literal(30)]).default(0),
});

/** Share-sheet Shortcut. At least one field must be present. */
export const captureBodySchema = z
  .object({
    url: z.string().max(2048).optional(),
    title: z.string().max(500).optional(),
    text: z.string().max(5000).optional(),
  })
  .refine((v) => Boolean(v.url || v.title || v.text), 'Send at least one of url, title or text');

/** Siri note Shortcut. */
export const noteBodySchema = z.object({ text: z.string().min(1).max(5000) });

/** Wallet automation: whatever iOS gives us. Stored as-is. */
export const walletBodySchema = z.record(z.string(), z.unknown());
